import { Polygon } from 'react-leaflet';
import {
  FEMA_IPAWS_OFFERED_JURISDICTION_PATH_OPTIONS,
  FEMA_IPAWS_SELECTED_JURISDICTION_PATH_OPTIONS,
} from 'features/Weather/features/WeatherAlerts/components/JurisdictionMapOverlay/JurisdictionMapOverlay';
import {
  getIpawsPolygonCenter,
  IPAWS_AREA_TYPES,
  moveIpawsPolygon,
  transformIpawsPolygon,
} from '../../../../../../utils/ipawsArea.utils';
import { isValidMapCoordinate } from '../../../../utils/alertMapEditor.utils';
import AlertPointMarker from '../../../AlertPointMarker/AlertPointMarker';
import OutsideCoordinateMarkers from '../../../OutsideCoordinateMarkers/OutsideCoordinateMarkers';
import IpawsAreaShapeLayer from '../IpawsAreaShapeLayer/IpawsAreaShapeLayer';
import IpawsPlacementPreview from '../IpawsPlacementPreview/IpawsPlacementPreview';
import IpawsShapeEditHandles from '../IpawsShapeEditHandles/IpawsShapeEditHandles';

// The areas the COG may alert (its profile's SAME codes) are FEMA jurisdictions: orange, dashed
// when offered, a solid heavier line when selected. Orientation only, never sent.

/**
 * Draws the saved IPAWS shapes, the temporary placement outline, and the handles for the selected
 * shape. All actual draft changes are sent back through callbacks.
 */
const IpawsAreaMapLayers = ({
  activeTool,
  authorizedAreas = [],
  displayedAreaShapes,
  displayedSelectedShape,
  onCommitPolygonVertexMove,
  onCommitSelectedShapeUpdate,
  onPreviewPolygonVertexMove,
  onPreviewSelectedShapeUpdate,
  onSelectShape,
  placementPreviewShape,
  selectedShape,
  selectedShapeId,
  selectedShapeLabel,
  severityColor,
  editMode,
  canInsertVertex,
  onInsertVertex,
  onRemoveVertex,
  outsideShapeIds = [],
  outsidePoints = [],
  isPlacementPreviewOutside = false,
}) => (
  <>
    {authorizedAreas.map((area) =>
      area.rings.map((ring, ringIndex) => (
        <Polygon
          key={`authorized-${area.key}-${ringIndex}`}
          positions={ring}
          pathOptions={
            area.selected
              ? FEMA_IPAWS_SELECTED_JURISDICTION_PATH_OPTIONS
              : FEMA_IPAWS_OFFERED_JURISDICTION_PATH_OPTIONS
          }
          // Orientation only. Clicks must reach the map and the shapes under it.
          interactive={false}
        />
      )),
    )}

    {displayedAreaShapes.map((shape) => (
      <IpawsAreaShapeLayer
        key={shape.id}
        shape={shape}
        activeTool={activeTool}
        selectedShapeId={selectedShapeId}
        severityColor={severityColor}
        onSelectShape={onSelectShape}
        editMode={shape.id === selectedShapeId ? editMode : null}
        canInsertVertex={canInsertVertex}
        onInsertVertex={onInsertVertex}
        isOutside={outsideShapeIds.includes(shape.id)}
      />
    ))}

    <IpawsPlacementPreview
      shape={placementPreviewShape}
      severityColor={severityColor}
      isOutside={isPlacementPreviewOutside}
    />

    {selectedShape?.type === IPAWS_AREA_TYPES.POLYGON ? (
      editMode === 'reshape' ? (
        selectedShape.coordinates?.map((position, vertexIndex) => (
          <AlertPointMarker
            key={`${selectedShape.id}-${vertexIndex}`}
            position={position}
            markerIndex={vertexIndex}
            accessibleName={`Move ${selectedShapeLabel} vertex ${vertexIndex + 1}`}
            showWorkplaceJurisdictionWarning={false}
            onMarkerMovePreview={onPreviewPolygonVertexMove}
            onMarkerMove={onCommitPolygonVertexMove}
          />
        ))
      ) : editMode === 'addVertex' || editMode === 'eraseVertex' ? (
        selectedShape.coordinates?.map((position, vertexIndex) => (
          <AlertPointMarker
            key={`${selectedShape.id}-${editMode}-${vertexIndex}`}
            position={position}
            markerIndex={vertexIndex}
            accessibleName={`${editMode === 'eraseVertex' ? 'Remove' : 'Existing'} ${selectedShapeLabel} vertex ${vertexIndex + 1}`}
            showWorkplaceJurisdictionWarning={false}
            draggable={false}
            onClick={editMode === 'eraseVertex' ? () => onRemoveVertex(vertexIndex) : undefined}
          />
        ))
      ) : (
        (() => {
          const center = getIpawsPolygonCenter(selectedShape.coordinates);
          if (!center) return null;
          const transformHandle = selectedShape.coordinates.reduce(
            (furthest, coordinate) =>
              Math.hypot(coordinate.lat - center.lat, coordinate.lng - center.lng) >
              Math.hypot(furthest.lat - center.lat, furthest.lng - center.lng)
                ? coordinate
                : furthest,
            selectedShape.coordinates[0],
          );
          const isTransform = editMode === 'resize' || editMode === 'rotate';
          return (
            <AlertPointMarker
              position={isTransform ? transformHandle : center}
              markerIndex={0}
              accessibleName={`${editMode === 'move' ? 'Move' : editMode === 'rotate' ? 'Rotate' : 'Resize'} ${selectedShapeLabel}`}
              showWorkplaceJurisdictionWarning={false}
              onMarkerMovePreview={(_index, position) =>
                onPreviewSelectedShapeUpdate({
                  coordinates: isTransform
                    ? transformIpawsPolygon(selectedShape.coordinates, position, editMode)
                    : moveIpawsPolygon(selectedShape.coordinates, position),
                })
              }
              onMarkerMove={(_index, position) =>
                onCommitSelectedShapeUpdate({
                  coordinates: isTransform
                    ? transformIpawsPolygon(selectedShape.coordinates, position, editMode)
                    : moveIpawsPolygon(selectedShape.coordinates, position),
                })
              }
            />
          );
        })()
      )
    ) : selectedShape && isValidMapCoordinate(selectedShape.center) ? (
      <IpawsShapeEditHandles
        shape={selectedShape}
        // The saved shape supplies identity; the display shape lets every handle follow the
        // temporary shape while the pointer is moving.
        displayShape={displayedSelectedShape}
        shapeLabel={selectedShapeLabel}
        onPreviewShape={onPreviewSelectedShapeUpdate}
        onUpdateShape={onCommitSelectedShapeUpdate}
        editMode={editMode}
      />
    ) : null}

    {/* Last, so each mark sits above the shapes and the edit handles. */}
    <OutsideCoordinateMarkers points={outsidePoints} />
  </>
);

export default IpawsAreaMapLayers;
